import axios from 'axios';

import { APIClient, getLoggedinUser } from '@/helpers/api_helper';
import config from '@/config';

import type {
  Lead,
  LeadCreatePayload,
  LeadListFilters,
  LeadListResponse,
  LeadUpdatePayload,
} from '../types';

/**
 * Leads API client — wraps the shared axios `APIClient` for JSON endpoints
 * and falls back to direct `axios` for the multipart `/Lead/WithAttachments`
 * endpoint (mirrors the legacy `leadAttachmentService.ts` pattern).
 *
 * Endpoints (per legacy `src/services/leadService.ts` + `leadStatusService.ts`):
 *   GET    /Lead?pageNumber=X&pageSize=Y
 *   GET    /Lead/ByStatus?status=X&pageNumber=X&pageSize=Y
 *   POST   /Lead                       — JSON
 *   POST   /Lead/WithAttachments       — multipart/form-data
 *   PUT    /Lead/:id
 *   DELETE /Lead/:id
 *   POST   /Lead/Approve
 *   POST   /Lead/ConvertToClient
 *   POST   /Lead/Churn
 */
const LEAD_BASE_URL = '/Lead';
const LEAD_BY_STATUS_URL = '/Lead/ByStatus';
const LEAD_WITH_ATTACHMENTS_URL = '/Lead/WithAttachments';
const LEAD_APPROVE_URL = '/Lead/Approve';
const LEAD_CONVERT_URL = '/Lead/ConvertToClient';
const LEAD_CHURN_URL = '/Lead/Churn';

const api = new APIClient();

const emptyListResponse = (filters: LeadListFilters): LeadListResponse => ({
  items: [],
  pageNumber: filters.pageNumber ?? 1,
  pageSize: filters.pageSize ?? 10,
  totalCount: 0,
  totalPages: 0,
  hasPreviousPage: false,
  hasNextPage: false,
});

/**
 * Normalise the pageable envelope — backend may return a partial shape on 204.
 */
function normaliseList(
  response: Partial<LeadListResponse> | undefined,
  filters: LeadListFilters,
): LeadListResponse {
  return {
    items: response?.items ?? [],
    pageNumber: response?.pageNumber ?? filters.pageNumber ?? 1,
    pageSize: response?.pageSize ?? filters.pageSize ?? 10,
    totalCount: response?.totalCount ?? 0,
    totalPages: response?.totalPages ?? 0,
    hasPreviousPage: response?.hasPreviousPage ?? false,
    hasNextPage: response?.hasNextPage ?? false,
  };
}

export const leadsApi = {
  list: async (filters: LeadListFilters = {}): Promise<LeadListResponse> => {
    const { pageNumber = 1, pageSize = 10, status } = filters;
    try {
      if (status !== undefined && status !== null) {
        const response = await api.get<LeadListResponse>(LEAD_BY_STATUS_URL, {
          status,
          pageNumber,
          pageSize,
        });
        return normaliseList(response, filters);
      }
      const response = await api.get<LeadListResponse>(LEAD_BASE_URL, {
        pageNumber,
        pageSize,
      });
      return normaliseList(response, filters);
    } catch (error) {
      // Return an empty page rather than throwing so the UI can show the
      // empty state without flicker; surface real errors through the query
      // error path by re-throwing if the underlying call threw a non-string.
      if (typeof error === 'string') {
        throw new Error(error);
      }
      if (error instanceof Error) {
        throw error;
      }
      return emptyListResponse(filters);
    }
  },

  create: (payload: LeadCreatePayload): Promise<Lead> => {
    const { attachments, ...json } = payload;
    if (attachments && attachments.length > 0) {
      return createLeadMultipart(payload);
    }
    return api.create<Lead>(LEAD_BASE_URL, json);
  },

  update: (id: string, payload: LeadUpdatePayload): Promise<Lead> => {
    return api.put<Lead>(`${LEAD_BASE_URL}/${id}`, payload);
  },

  remove: (id: string): Promise<void> => {
    return api.delete<void>(`${LEAD_BASE_URL}/${id}`);
  },

  approve: (leadId: string): Promise<Lead> => {
    return api.create<Lead>(LEAD_APPROVE_URL, { leadId });
  },

  convertToClient: (
    leadId: string,
    outcomeReason: number,
    outcomeNotes: string,
  ): Promise<Lead> => {
    return api.create<Lead>(LEAD_CONVERT_URL, {
      leadId,
      isBackground: false,
      outcomeReason,
      outcomeNotes,
    });
  },

  churn: (
    leadId: string,
    outcomeReason: number,
    outcomeNotes: string,
  ): Promise<Lead> => {
    return api.create<Lead>(LEAD_CHURN_URL, {
      leadId,
      outcomeReason,
      outcomeNotes,
    });
  },
};

/**
 * `POST /Lead/WithAttachments` — multipart create with file attachments.
 *
 * Bypasses the shared APIClient because it sends JSON-only by default; we use
 * axios directly with the persisted auth token, mirroring
 * `src/services/leadAttachmentService.ts`. The backend wraps the response in
 * `{ isSuccess, result, message, modelErrors }` so we unwrap before returning.
 */
async function createLeadMultipart(payload: LeadCreatePayload): Promise<Lead> {
  const formData = new FormData();
  if (payload.tenantId) formData.append('TenantId', payload.tenantId);
  if (payload.userId) formData.append('UserId', payload.userId);
  if (payload.title) formData.append('Title', payload.title);
  if (payload.contactPerson)
    formData.append('ContactPerson', payload.contactPerson);
  if (payload.contactEmail)
    formData.append('ContactEmail', payload.contactEmail);
  if (payload.description) formData.append('Description', payload.description);
  formData.append('LeadStatus', String(payload.leadStatus));
  formData.append('TentativeWorkDays', String(payload.tentativeWorkDays));
  if (payload.notes) formData.append('Notes', payload.notes);
  if (payload.phoneNumber) formData.append('PhoneNumber', payload.phoneNumber);
  if (payload.siteAddress) formData.append('SiteAddress', payload.siteAddress);
  if (payload.tenantLocationId)
    formData.append('TenantLocationId', payload.tenantLocationId);
  if (payload.siteTypeId) formData.append('SiteTypeId', payload.siteTypeId);
  if (payload.tentativeProjectStartDate)
    formData.append(
      'TentativeProjectStartDate',
      payload.tentativeProjectStartDate,
    );

  (payload.attachments ?? []).forEach((file) => {
    formData.append('Attachments', file);
  });

  const authUser = getLoggedinUser();
  const token: string | undefined = authUser?.token;

  const response = await axios.post(
    `${config.api.API_URL}${LEAD_WITH_ATTACHMENTS_URL}`,
    formData,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  // Axios response interceptor in api_helper.ts already unwraps `response.data`,
  // but multipart bypasses some normalisation; handle either shape.
  const responseData =
    (response as unknown as { data?: unknown })?.data ?? response;
  const envelope = responseData as {
    isSuccess?: boolean;
    message?: string;
    modelErrors?: Array<{ key: string; value: string }>;
    result?: Lead;
    id?: string;
  };

  if (envelope?.isSuccess === false) {
    const details = (envelope.modelErrors ?? [])
      .map((err) => `${err.key}: ${err.value}`)
      .join(', ');
    throw new Error(
      details
        ? `${envelope.message ?? 'Failed to create lead'}. ${details}`
        : envelope.message ?? 'Failed to create lead',
    );
  }

  if (envelope?.result?.id) {
    return envelope.result;
  }
  if (envelope?.id) {
    return envelope as unknown as Lead;
  }

  throw new Error(envelope?.message ?? 'Invalid response from server');
}
